/**
 * Worksite progress chart (per quote/situation).
 * Visualizes : cumulative invoiced HT over time vs linear planned progress,
 * with an "on track" indicator that compares current cumulative % to the
 * elapsed planned % between project start_date and end_date.
 *
 * Use case : on the project facturation tab, see in 1 glance if the
 * worksite is over or under-billed compared to plan.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { TrendUp, Warning, CheckCircle, ChartLine } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const fmtPct = (v) => `${Number(v || 0).toFixed(1)} %`;

const ProgressChart = ({ quoteId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/invoicing/quotes/${quoteId}/progress-chart`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  // Compute planned % at today's date (linear schedule from start to end)
  const plannedPctNow = useMemo(() => {
    if (!data?.planned?.start || !data?.planned?.end) return null;
    const s = new Date(data.planned.start).getTime();
    const e = new Date(data.planned.end).getTime();
    const now = Date.now();
    if (!isFinite(s) || !isFinite(e) || e <= s) return null;
    const t = Math.min(100, Math.max(0, ((now - s) / (e - s)) * 100));
    return Number(t.toFixed(1));
  }, [data]);

  // Build chart series — actual cumulative + planned linear line + bar per situation
  const chartData = useMemo(() => {
    if (!data) return [];
    const series = (data.points || []).map((p) => ({
      label: p.number || `S${p.position}`,
      date: p.date,
      cumulatif: p.cumulative_ht,
      situation: p.situation_ht,
      pct: p.cumulative_pct,
    }));
    return series;
  }, [data]);

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#5E5F66', fontSize: '0.86rem' }} data-testid="progress-chart-loading">
        Chargement du graphique...
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 16, background: '#FEE2E2', color: '#991B1B', borderRadius: 8, fontSize: '0.84rem' }}>{error}</div>
    );
  }
  if (!data || data.situations_count === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#5E5F66', fontSize: '0.86rem', background: '#F8F9FB', borderRadius: 8 }}>
        <ChartLine size={28} color="#9CA3AF" />
        <div style={{ marginTop: 8 }}>Aucune situation emise pour ce devis.</div>
        <div style={{ fontSize: '0.78rem', marginTop: 4, color: '#9CA3AF' }}>
          Le graphique apparaitra des la 1<sup>re</sup> facture de situation.
        </div>
      </div>
    );
  }

  // Status comparison (only when we have a planned series)
  let statusIcon = TrendUp;
  let statusColor = '#1D4ED8';
  let statusText = 'En cours';
  if (plannedPctNow !== null) {
    const diff = data.current_cumulative_pct - plannedPctNow;
    if (diff >= -5 && diff <= 10) {
      statusIcon = CheckCircle;
      statusColor = '#16A34A';
      statusText = 'Dans le plan';
    } else if (diff > 10) {
      statusIcon = TrendUp;
      statusColor = '#D97706';
      statusText = 'En avance de facturation';
    } else {
      statusIcon = Warning;
      statusColor = '#DC2626';
      statusText = `En retard de ${Math.abs(diff).toFixed(1)} pts`;
    }
  }
  const StatusIcon = statusIcon;

  return (
    <div data-testid="progress-chart" style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ChartLine size={18} color="#002FA7" weight="bold" /> Avancement chantier
          </h3>
          <div style={{ fontSize: '0.76rem', color: '#5E5F66', marginTop: 2 }}>
            Devis {data.quote_number} - {data.project_name}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>Marche HT</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A' }}>{fmtEur(data.marche_ht)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>Cumule facture HT</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1D4ED8' }}>
              {fmtEur(data.current_cumulative_ht)} <span style={{ fontSize: '0.78rem', color: '#5E5F66', fontWeight: 500 }}>({fmtPct(data.current_cumulative_pct)})</span>
            </div>
          </div>
          <div data-testid="progress-status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: `${statusColor}1A`, color: statusColor, fontWeight: 700, fontSize: '0.82rem' }}>
            <StatusIcon size={14} weight="bold" /> {statusText}
          </div>
        </div>
      </div>

      <div style={{ height: 280, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E6" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'Avancement') return [`${value} %`, name];
                return [`${Number(value).toLocaleString('fr-FR')} €`, name];
              }}
              labelFormatter={(lbl) => `Situation ${lbl}`}
              contentStyle={{ fontSize: '0.82rem', borderRadius: 8, border: '1px solid #E2E2E6' }}
            />
            <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
            <Area yAxisId="left" type="monotone" dataKey="cumulatif" name="Cumule facture HT" fill="#002FA7" fillOpacity={0.15} stroke="#002FA7" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="pct" name="Avancement" stroke="#16A34A" strokeWidth={2} dot={{ r: 4 }} />
            {plannedPctNow !== null && (
              <ReferenceLine yAxisId="right" y={plannedPctNow} stroke="#9CA3AF" strokeDasharray="6 4" label={{ value: `Plan: ${plannedPctNow}%`, position: 'right', fontSize: 10, fill: '#5E5F66' }} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ marginTop: 12, padding: '10px 12px', background: '#F8FAFC', borderRadius: 8, fontSize: '0.78rem', color: '#5E5F66', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div><strong>{data.situations_count}</strong> situation{data.situations_count > 1 ? 's' : ''} emise{data.situations_count > 1 ? 's' : ''}</div>
        {data.planned.start && <div>Demarrage prevu : <strong>{data.planned.start.split('-').reverse().join('/')}</strong></div>}
        {data.planned.end && <div>Fin prevue : <strong>{data.planned.end.split('-').reverse().join('/')}</strong></div>}
        {plannedPctNow !== null && <div>Avancement plan a date : <strong>{plannedPctNow} %</strong></div>}
      </div>
    </div>
  );
};

export default ProgressChart;
